import axios from "axios";
import { API_REQUEST, apiSuccess, apiError } from "../../dux/api";
import {
  getAccessToken,
  getRefreshToken,
  isTokenExpiringSoon,
  setTokens,
  getSession,
} from "../../helpers/sessionStorageHelper";
import { getTokenRefreshUrl } from "../../helpers/apiEndPoints";
import { v4 } from "uuid";
import { getBaseApiUrl } from "../../helpers/generic";
import { apiCallStart, apiCallComplete } from "../../dux/apiStatus";

// Ensure only one refresh request runs at a time
let ongoingRefresh = null;

async function refreshTokensOnce(dispatch) {
  if (ongoingRefresh) return ongoingRefresh;
  const session = getSession();
  const refresh_token = getRefreshToken();
  if (!session?.userData?.user?.id || !refresh_token) return null;

  ongoingRefresh = (async () => {
    try {
      dispatch(apiCallStart({ api: "tokenRefresh" }));
      const refreshResp = await axios.post(
        getTokenRefreshUrl(),
        {
          user_id: session.userData.user.id,
          refresh_token,
        },
        {
          headers: {
            ...(getAccessToken()
              ? { Authorization: `Bearer ${getAccessToken()}` }
              : {}),
          },
        }
      );
      const data = refreshResp?.data?.response || refreshResp?.data;
      if (data?.access_token) {
        setTokens({
          access_token: data.access_token,
          refresh_token: data.refresh_token,
        });
      }
      dispatch(apiCallComplete({ api: "tokenRefresh" }));
      return data;
    } catch (e) {
      dispatch(apiCallComplete({ api: "tokenRefresh" }));
      throw e;
    } finally {
      ongoingRefresh = null;
    }
  })();

  return ongoingRefresh;
}

const apiMiddleware =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    next(action);

    if (!action.type.includes(API_REQUEST)) return;

    const { url, method, data, headers, feature, meta, responseType } =
      action.payload;
    let requestUrl = url;
    const accessToken = getAccessToken();
    // For DELETE requests, we don't send data in the body
    // For GET requests, we send data as params
    // For other methods (POST, PUT, PATCH), we send data in the body
    let requestConfig = {
      url: requestUrl,
      method,
      headers: {
        ...headers,
        "Content-Type": "application/json",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      responseType,
    };

    if (method === "GET") {
      requestConfig.params = data;
    } else if (data) {
      // Allow bodies for POST/PUT/PATCH/DELETE when provided
      requestConfig.data = data;
    }
    // For DELETE requests, we explicitly don't add data field

    // axios default configs
    axios.defaults.baseURL = getBaseApiUrl() || "http://localhost:3002/api/v1";

    // if (accessToken) {
    //   requestUrl = `${url}?access_token=${accessToken}`;
    // }

    const doRequest = () => {
      const latest = getAccessToken();
      if (latest) {
        requestConfig.headers.Authorization = `Bearer ${latest}`;
      } else {
        delete requestConfig.headers.Authorization;
      }
      return axios.request(requestConfig);
    };

    const performWithRefresh = async () => {
      // Proactive refresh (skip if this call itself is the refresh endpoint)
      const isRefreshCall =
        typeof requestUrl === "string" &&
        requestUrl.includes("/api/v1/user/token/refresh");
      const skipProactiveRefresh = meta && meta.skipProactiveRefresh === true;
      // Use a strict 2-minute pre-expiry window for proactive refreshes
      const PROACTIVE_WINDOW_SECONDS = 120;
      try {
        if (
          !isRefreshCall &&
          !skipProactiveRefresh &&
          getAccessToken() &&
          isTokenExpiringSoon(PROACTIVE_WINDOW_SECONDS)
        ) {
          const data = await refreshTokensOnce(dispatch);
          if (data?.access_token) {
            requestConfig.headers.Authorization = `Bearer ${data.access_token}`;
          }
        }
      } catch (e) {
        // If proactive refresh fails, continue; reactive handler below will catch 401s
      }

      try {
        return await doRequest();
      } catch (error) {
        // Reactive refresh on 401/999
        const status = error?.response?.status;
        if (!isRefreshCall && (status === 999 || status === 401)) {
          try {
            const data = await refreshTokensOnce(dispatch);
            if (data?.access_token) {
              // retry original request with new token
              requestConfig.headers.Authorization = `Bearer ${data.access_token}`;
              return await doRequest();
            }
          } catch (refreshErr) {
            // fall through to error dispatch
          }
        }
        throw error;
      }
    };

    performWithRefresh()
      .then((axiosResponse) => {
        if (axiosResponse.status === 200 && responseType === "blob") {
          const blob = new Blob([axiosResponse.data], { type: responseType });
          const url = URL.createObjectURL(blob);

          // Create a temporary link element
          const link = document.createElement("a");
          link.href = url;
          let filename = "output";
          if (feature === "INVOICE_DOWNLOAD") {
            filename = `invoice-${v4()}.pdf`;
          } else if (
            feature === "DOWNLOAD_BOOKING_RECORD_CSV" ||
            feature === "DOWNLOAD_BOOKING_RECORD_CSV_FILE"
          ) {
            filename = "output.csv";
          }
          link.download = filename;

          // Append the link to the body and click it to trigger the download
          document.body.appendChild(link);
          link.click();

          // Clean up: remove the link and revoke the object URL
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        } else if (url.includes("invoice")) {
          // Handle error response
          console.error(
            "File download failed:",
            axiosResponse.status,
            axiosResponse.statusText
          );
        }

        // Optional per-call success callback
        try {
          if (meta && typeof meta.onSuccess === "function") {
            meta.onSuccess(axiosResponse.data);
          }
        } catch (_) {}

        dispatch(
          apiSuccess({
            response: axiosResponse.data,
            feature,
            meta,
          })
        );
      })
      .catch((error) => {
        // Optional per-call error callback
        try {
          if (meta && typeof meta.onError === "function") {
            meta.onError(error?.response || error);
          }
        } catch (_) {}

        dispatch(
          apiError({
            error,
            response: error.response,
            feature,
            meta,
          })
        );
      });
    // }
  };

export default apiMiddleware;
